import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { map, Observable } from "rxjs";
import { AuthService } from "./auth.service";
import { AuthResponse, LoginCredentials } from "../interfaces/auth.interface";
import { environment } from "../../../../environments/environment.dev";
import { User } from "../interfaces/user.interface";
import { UserStats } from "../interfaces/user-stats.interface";
import {
  RegistrationRequest,
  RegistrationResponse,
} from "../interfaces/registration-request.interface";
import { ApiResponse } from "../../../core/interfaces/ApiResponse";
import { HttpParams } from "@angular/common/http";
import { convertToHttpParams } from "../../../core/interfaces/common-search-param.interface";

@Injectable({
  providedIn: "root",
})
export class AuthApiService extends AuthService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}v1/admin-auth`;

  constructor() {
    super();
  }

  login(credentials: LoginCredentials): Observable<AuthResponse> {
    return this.wrapRequest(
      this.http
        .post<ApiResponse<AuthResponse>>(
          `${this.baseUrl}/sign-in`,
          credentials,
          {
            headers: this.headers,
          },
        )
        .pipe(map((res) => res?.response)),
    );
  }

  logout(): Observable<void> {
    return this.wrapRequest(
      this.http.delete<void>(`${this.baseUrl}/sign-out`, {
        headers: this.headers,
      }),
    );
  }

  forgotPassword(email: string): Observable<void> {
    return this.wrapRequest(
      this.http.post<void>(`${this.baseUrl}/forgot-password`, null, {
        headers: this.headers,
        params: { email },
      }),
    );
  }

  verifyOtp(
    uniqueId: string,
    contactType: string,
    otpCode: string,
  ): Observable<void> {
    return this.wrapRequest(
      this.http.post<void>(
        `${this.baseUrl}/verify-user-otp`,
        { uniqueId, contactType, otpCode },
        { headers: this.headers },
      ),
    );
  }

  resetPassword(token: string, newPassword: string): Observable<void> {
    return this.wrapRequest(
      this.http.post<void>(
        `${this.baseUrl}/reset-password`,
        { token, newPassword },
        { headers: this.headers },
      ),
    );
  }

  currentUser(): Observable<User> {
    return this.wrapRequest(
      this.http
        .get<ApiResponse<User>>(`${environment.apiBaseUrl}v1/users/user-info`, {
          headers: this.headers,
        })
        .pipe(map((res: any) => res?.response || res)),
      false,
      false
    );
  }

  currentUserStats(): Observable<UserStats> {
    return this.wrapRequest(
      this.http
        .get<ApiResponse<UserStats>>(
          `${environment.apiBaseUrl}v1/user/user-stats`,
          {
            headers: this.headers,
          },
        )
        .pipe(map((res: any) => res?.response || res)),
      false,
      false
    );
  }

  verifyEmail(otp: string): Observable<void> {
    return this.wrapRequest(
      this.http.post<void>(
        `${this.baseUrl}/verify-email`,
        { otp },
        { headers: this.headers },
      ),
    );
  }

  resendEmailOtp(): Observable<void> {
    return this.wrapRequest(
      this.http.post<void>(
        `${this.baseUrl}/resend/email-otp`,
        {},
        { headers: this.headers },
      ),
    );
  }

  updateEmail(email: string): Observable<void> {
    return this.wrapRequest(
      this.http.post<void>(
        `${this.baseUrl}/update/email`,
        { email },
        { headers: this.headers },
      ),
    );
  }

  verifyPhone(uniqueId: string, otp: string): Observable<void> {
    return this.wrapRequest(
      this.http.post<void>(
        `${this.baseUrl}/verify-otp`,
        { uniqueId, otpCode: otp, contactType: "PHONE" },
        { headers: this.headers },
      ),
    );
  }

  resendPhoneOtp(params: {
    uniqueId: string;
    contactType: string;
  }): Observable<boolean> {
    const httpParams = convertToHttpParams(params);

    return this.wrapRequest(
      this.http.post<boolean>(`${this.baseUrl}/send-otp`, null, {
        headers: this.headers,
        params: httpParams,
      }),
    );
  }

  updatePhone(phone: string): Observable<string> {
    return this.wrapRequest(
      this.http
        .put<ApiResponse<any>>(`${this.baseUrl}/update-phone`, null, {
          headers: this.headers,
          params: { phone },
        })
        .pipe(
          map((res) => {
            const data = res?.response;
            if (typeof data === "object" && data !== null) {
              return data.uniqueId || data.id || "";
            }
            return data || "";
          }),
        ),
    );
  }

  acceptTerms(data: {
    isAgreed: boolean;
    isSubscribeTips: boolean;
    isSubscribeCommunity: boolean;
  }): Observable<void> {
    return this.wrapRequest(
      this.http.post<void>(
        `${this.baseUrl}/accept-terms`,
        {
          Agreed: data.isAgreed,
          subsribeTips: data.isSubscribeTips,
          subscribeCommunity: data.isSubscribeCommunity,
        },
        { headers: this.headers },
      ),
    );
  }

  signup(
    registrationRequest: RegistrationRequest,
  ): Observable<RegistrationResponse> {
    return this.wrapRequest(
      this.http.post<RegistrationResponse>(
        `${environment.apiBaseUrl}v1/user-registration/register`,
        registrationRequest,
        { headers: this.headers },
      ),
    );
  }

  updateRegistrationEmail(
    uniqueId: string,
    email: string,
  ): Observable<boolean> {
    return this.wrapRequest(
      this.http.post<boolean>(
        `${this.baseUrl}/update-registration-email`,
        { uniqueId, email },
        { headers: this.headers },
      ),
    );
  }

  updateRegistrationPhone(
    uniqueId: string,
    phone: string,
  ): Observable<boolean> {
    return this.wrapRequest(
      this.http.put<boolean>(
        `${this.baseUrl}/update-registration-phone`,
        null,
        {
          headers: this.headers,
          params: {
            uniqueId,
            phone,
          },
        },
      ),
    );
  }

  resendRegistrationEmailOtp(uniqueId: string): Observable<boolean> {
    return this.wrapRequest(
      this.http.post<boolean>(`${this.baseUrl}/send-otp`, null, {
        headers: this.headers,
        params: { uniqueId, contactType: "EMAIL" },
      }),
    );
  }

  resendRegistrationPhoneOtp(uniqueId: string): Observable<boolean> {
    return this.wrapRequest(
      this.http.post<boolean>(`${this.baseUrl}/send-otp`, null, {
        headers: this.headers,
        params: { uniqueId, contactType: "PHONE" },
      }),
    );
  }

  verifyRegistrationEmailOtp(
    uniqueId: string,
    otp: string,
  ): Observable<boolean> {
    return this.wrapRequest(
      this.http.post<boolean>(
        `${this.baseUrl}/verify-otp`,
        { uniqueId, otpCode: otp, contactType: "EMAIL" },
        { headers: this.headers },
      ),
    );
  }

  verifyRegistrationPhoneOtp(
    uniqueId: string,
    otp: string,
  ): Observable<boolean> {
    return this.wrapRequest(
      this.http.post<boolean>(
        `${this.baseUrl}/verify-otp`,
        { uniqueId, otpCode: otp, contactType: "PHONE" },
        { headers: this.headers },
      ),
    );
  }

  acceptRegistrationTerms(
    uniqueId: string,
    data: {
      isAgreed: boolean;
      isSubscribeTips: boolean;
      isSubscribeCommunity: boolean;
    },
  ): Observable<RegistrationResponse> {
    return this.wrapRequest(
      this.http.post<RegistrationResponse>(
        `${this.baseUrl}/accept-registration-terms`,
        { uniqueId, ...data },
        { headers: this.headers },
      ),
    );
  }

  updateFcmToken(token: string): Observable<any> {
    const params = { token };
    return this.wrapRequest(
      this.http.post<any>(`${this.baseUrl}/fcm-token`, null, {
        headers: this.headers,
        params: params,
      }),
    );
  }

  getInviteDetails(inviteId: string): Observable<RegistrationRequest> {
    return this.wrapRequest(
      this.http.get<RegistrationRequest>(`${this.baseUrl}/invite/${inviteId}`, {
        headers: this.headers,
      }),
    );
  }

  signInByRegistration(uniqueId: string): Observable<AuthResponse> {
    return this.wrapRequest(
      this.http
        .post<ApiResponse<AuthResponse>>(
          `${this.baseUrl}/sign-in-by-registration`,
          null,
          {
            headers: this.headers,
            params: { uniqueId },
          },
        )
        .pipe(map((res) => res?.response)),
    );
  }
}
